import { type ApolloError, isApolloError } from '@apollo/client';
import type { GraphQLFormattedError } from 'graphql';
import { get, has } from 'lodash';
import { getI18n } from 'react-i18next';
import { assert } from 'ts-essentials';

type ErrorCodeExtension = string;

const NETWORK_ERROR_MESSAGE = 'Network Error';
const APOLLO_NETWORK_ERROR_MESSAGE = 'Failed to fetch';
const CHUNK_LOAD_ERROR_NAME = 'ChunkLoadError';
const DYNAMIC_IMPORT_ERROR_MESSAGE_PREFIX = 'Failed to fetch dynamically imported module';

const isGeneralError = (graphQLError: GraphQLFormattedError) =>
  ['INTERNAL_SERVER_ERROR', 'GENERIC_ERROR'].includes(graphQLError.extensions?.code as string);

export const isNetworkError = (error: Error) =>
  error.message === NETWORK_ERROR_MESSAGE ||
  error.message === APOLLO_NETWORK_ERROR_MESSAGE ||
  error.name === CHUNK_LOAD_ERROR_NAME ||
  error.message.startsWith(DYNAMIC_IMPORT_ERROR_MESSAGE_PREFIX);

export const doesErrorMatchCode = (
  error: { graphQLErrors: readonly GraphQLFormattedError[] },
  code: string,
): boolean => error?.graphQLErrors.some((gqlError) => gqlError.extensions?.code === code);

export const getErrorExtension = <T>(
  error: { graphQLErrors: readonly GraphQLFormattedError[] },
  path: string,
): T => {
  const errorWithPath = error.graphQLErrors.find((gqlError) => has(gqlError.extensions, path));
  const extension = get(errorWithPath?.extensions, path);

  assert(extension, 'Unable to find error extension.');

  return extension as T;
};

export const isGraphQLError = (
  error: Partial<GraphQLFormattedError> | null | undefined,
): error is GraphQLFormattedError => {
  const hasMessage = typeof error?.message === 'string';
  const hasPath = Array.isArray(error?.path);
  const hasCode = typeof error?.extensions?.code === 'string';
  return hasMessage && hasPath && hasCode;
};

interface ErrorMessage {
  title?: string;
  message: string;
  severity?: 'warning' | 'danger';
}

export const extractGraphQLErrorDetailsToDisplay = (
  error: Error | ApolloError | GraphQLFormattedError,
  customErrorMap?: Record<ErrorCodeExtension, ErrorMessage>,
): ErrorMessage | null => {
  const i18n = getI18n();
  const errorTranslations = {
    generic: () => ({
      title: i18n.t('genericError.title'),
      message: i18n.t('genericError.message'),
    }),
    network: () => ({
      title: i18n.t('networkError.title'),
      message: i18n.t('networkError.message'),
    }),
    custom: (message: string) => ({
      title: i18n.t('error'),
      message,
    }),
  };

  if (error instanceof Error && isNetworkError(error)) {
    return errorTranslations.network();
  }

  if (error instanceof Error && isApolloError(error)) {
    if (error.networkError) {
      return null;
    }

    if (customErrorMap) {
      for (const [code, res] of Object.entries(customErrorMap)) {
        if (doesErrorMatchCode(error, code)) {
          return res;
        }
      }
    }

    const uniqueError = error.graphQLErrors.find(
      (graphQLError) => graphQLError.extensions && !isGeneralError(graphQLError),
    );
    if (uniqueError) {
      return errorTranslations.custom(error.message);
    }

    return errorTranslations.generic();
  }

  if (isGraphQLError(error)) {
    if (isGeneralError(error)) {
      return errorTranslations.generic();
    }

    return errorTranslations.custom(error.message);
  }

  return null;
};
